import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Plus, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import {
  TOOL_REGISTRY,
  TOOL_GROUP_LABELS,
  getToolDefinition,
  type ToolGroup,
  type ToolStatus,
  type ToolLibraryEntry,
  type ToolGrant,
  type ToolDefinition,
} from '@platform/shared-types';
import { InventoryPage, RUBY, RUBY_DEEP } from '../inventory/InventoryChrome';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { getIcon } from '../../services/iconRegistry';
import { toastService } from '../../services/toastService';
import { toolsApi, type ToolTenantHit } from '../../services/toolsApi';
import { TenantPicker } from '../../components/tools/TenantPicker';

/**
 * Super Admin → Tools → Tool Library.
 *
 * Internal tools are NOT sold — no Stripe item, never in packages, signup or
 * merchant billing. A tool is on for a tenant when a ToolGrant is enabled.
 *
 * The registry (TOOL_REGISTRY) is the source of which tools exist; the API
 * adds tenant counts and grants. If tools-service is unreachable the page
 * still renders every registry tool, with an inline error banner.
 */

type GroupFilter = 'all' | ToolGroup;

const GROUP_ORDER: ToolGroup[] = ['catalog_cleanup', 'import_migration'];

const STATUS_PILL: Record<ToolStatus, { label: string; cls: string }> = {
  live: { label: 'LIVE', cls: 'bg-emerald-50 text-emerald-700' },
  in_build: { label: 'IN BUILD', cls: 'bg-sky-50 text-sky-700' },
  planned: { label: 'PLANNED', cls: 'bg-stone-100 text-stone-500' },
};

function mergeLibrary(apiTools: ToolLibraryEntry[] | null): ToolLibraryEntry[] {
  const byKey = new Map((apiTools || []).map((t) => [t.key, t]));
  const merged: ToolLibraryEntry[] = TOOL_REGISTRY.map((def) => {
    const api = byKey.get(def.key);
    return { ...def, ...(api || {}), tenantCount: api?.tenantCount ?? 0 };
  });
  // A tool the backend knows about that this build's registry does not yet.
  for (const t of apiTools || []) {
    if (!TOOL_REGISTRY.some((d) => d.key === t.key)) merged.push(t);
  }
  return merged;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
}

function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Contract chips — Can use / Takes / Gives / Plugs into ───────────────────
const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 border border-stone-200">{children}</span>
);
const Cap = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{children}</span>
);

function ContractRow({ tool }: { tool: ToolDefinition }) {
  const uses = (tool.canUse || []).map((k) => getToolDefinition(k)?.name || k);
  if (!tool.takes && !tool.gives && !tool.plugsInto?.length && !uses.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      {uses.length > 0 && (<><Cap>Can use</Cap>{uses.map((u) => <Chip key={u}>{u}</Chip>)}</>)}
      {tool.takes && (<><Cap>Takes</Cap><Chip>{tool.takes}</Chip></>)}
      {tool.gives && (<><Cap>Gives</Cap><Chip>{tool.gives}</Chip></>)}
      {!!tool.plugsInto?.length && (<><Cap>Plugs into</Cap>{tool.plugsInto.map((p) => <Chip key={p}>{p}</Chip>)}</>)}
    </div>
  );
}

// ── Custom switch (never a native checkbox) — 44px hit area ─────────────────
const Switch: React.FC<{ on: boolean; busy?: boolean; onClick: () => void; label: string }> = ({ on, busy, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={busy}
    onClick={onClick}
    className="h-11 w-14 flex items-center justify-center shrink-0 disabled:opacity-60"
  >
    <span
      className="w-12 h-7 rounded-full relative transition-colors"
      style={{ background: on ? RUBY : '#d6d3d1' }}
    >
      <span
        className="w-6 h-6 bg-white rounded-full absolute top-0.5 shadow flex items-center justify-center transition-all"
        style={{ left: on ? 'calc(100% - 26px)' : '2px', color: RUBY }}
      >
        {busy && <RubyGemMark size={14} drawing color="currentColor" />}
      </span>
    </span>
  </button>
);

// ── Custom confirm modal (never window.confirm) ─────────────────────────────
const ConfirmModal: React.FC<{
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ title, body, confirmLabel, busy, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50" onClick={busy ? undefined : onCancel}>
    <div
      role="dialog"
      aria-modal="true"
      className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 pt-5 pb-3 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: '#FFF1F3', color: RUBY }}>
          <AlertTriangle size={20} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-stone-900">{title}</h3>
          <div className="text-sm text-stone-500 mt-1">{body}</div>
        </div>
      </div>
      <div className="px-5 py-4 bg-stone-50 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="h-11 px-4 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:bg-white disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="h-11 px-4 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-70"
          style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
        >
          {busy && <RubyGemMark size={16} drawing color="currentColor" />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ── Page ────────────────────────────────────────────────────────────────────
const ToolLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const [apiTools, setApiTools] = useState<ToolLibraryEntry[] | null>(null);
  const [grants, setGrants] = useState<Record<string, ToolGrant[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<GroupFilter>('all');
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ toolKey: string; toolName: string; grant: ToolGrant } | null>(null);
  const [disabling, setDisabling] = useState(false);

  const tools = useMemo(() => mergeLibrary(apiTools), [apiTools]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const lib = await toolsApi.library();
      const list = lib.tools || [];
      setApiTools(list);
      const live = mergeLibrary(list).filter((t) => t.status === 'live');
      const results = await Promise.allSettled(live.map((t) => toolsApi.toolTenants(t.key)));
      const next: Record<string, ToolGrant[]> = {};
      const failed: string[] = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[live[i].key] = (r.value.grants || []).filter((g) => g.enabled);
        else failed.push(live[i].name);
      });
      setGrants(next);
      if (failed.length) setError(`Couldn't load enabled tenants for ${failed.join(', ')}.`);
    } catch (e: any) {
      setApiTools(null);
      setGrants({});
      setError(`Tools service unreachable — showing the tool registry only. ${e?.message || ''}`.trim());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const liveCount = tools.filter((t) => t.status === 'live').length;
    const tenantIds = new Set<string>();
    Object.values(grants).forEach((gs) => gs.forEach((g) => tenantIds.add(g.tenantId)));
    const tenantsUsing = tenantIds.size || tools.reduce((n, t) => Math.max(n, t.tenantCount || 0), 0);
    return { total: tools.length, live: liveCount, tenantsUsing };
  }, [tools, grants]);

  const enable = async (toolKey: string, hit: ToolTenantHit) => {
    try {
      const { grant } = await toolsApi.setGrant(toolKey, hit.tenantId, { enabled: true });
      const g: ToolGrant = { ...grant, tenantName: grant.tenantName || hit.name };
      setGrants((prev) => ({
        ...prev,
        [toolKey]: [...(prev[toolKey] || []).filter((x) => x.tenantId !== g.tenantId), g],
      }));
      setApiTools((prev) => (prev || mergeLibrary(null)).map((t) =>
        t.key === toolKey ? { ...t, tenantCount: (t.tenantCount || 0) + 1 } : t));
      toastService.success(`Enabled for ${g.tenantName || g.tenantId}`);
      setPickerFor(null);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not enable the tool');
    }
  };

  const disable = async () => {
    if (!confirm) return;
    const { toolKey, grant } = confirm;
    setDisabling(true);
    try {
      await toolsApi.setGrant(toolKey, grant.tenantId, { enabled: false });
      setGrants((prev) => ({ ...prev, [toolKey]: (prev[toolKey] || []).filter((g) => g.tenantId !== grant.tenantId) }));
      setApiTools((prev) => (prev || mergeLibrary(null)).map((t) =>
        t.key === toolKey ? { ...t, tenantCount: Math.max(0, (t.tenantCount || 0) - 1) } : t));
      toastService.success(`Disabled for ${grant.tenantName || grant.tenantId}`);
      setConfirm(null);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not disable the tool');
    } finally {
      setDisabling(false);
    }
  };

  const groups = GROUP_ORDER.filter((g) => filter === 'all' || filter === g);
  const filterTabs: { id: GroupFilter; label: string }[] = [
    { id: 'all', label: 'All tools' },
    ...GROUP_ORDER.map((g) => ({ id: g as GroupFilter, label: TOOL_GROUP_LABELS[g] })),
  ];

  const statsBar = (
    <div className="flex items-center gap-5 text-sm flex-wrap">
      <span className="text-neutral-400">Tools <b className="text-white">{stats.total}</b></span>
      <span className="text-neutral-400">Live <b className="text-emerald-400">{stats.live}</b></span>
      <span className="text-neutral-400">Tenants using a tool <b className="text-white">{stats.tenantsUsing}</b></span>
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="h-11 w-11 grid place-items-center rounded-lg border border-neutral-700 text-neutral-300 hover:text-white disabled:opacity-60"
        aria-label="Refresh"
        title="Refresh"
      >
        {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
      </button>
    </div>
  );

  const pills = (
    <div className="flex items-center gap-2 overflow-x-auto text-sm">
      {filterTabs.map((t) => {
        const on = filter === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setFilter(t.id)}
            className={`h-11 px-4 rounded-full whitespace-nowrap font-medium transition ${on ? 'text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}
            style={on ? { background: RUBY } : undefined}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <InventoryPage icon={Wrench} title="Tool Library" crumb="Tools" actions={statsBar} tabs={pills}>
      <div className="space-y-6 max-w-6xl">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={load} className="h-8 px-3 -my-1 rounded-md border border-rose-300 text-xs font-medium hover:bg-white">Retry</button>
          </div>
        )}

        <div className="rounded-lg px-4 py-3 text-sm border border-dashed border-amber-400 bg-amber-50 text-amber-900">
          Internal tools are <b>not sold</b> — no Stripe item, never in the catalog, signup or merchant billing. Only super admins enable them.
        </div>

        {groups.map((group) => {
          const groupTools = tools.filter((t) => t.group === group);
          if (!groupTools.length) return null;
          const liveTools = groupTools.filter((t) => t.status === 'live');
          const otherTools = groupTools.filter((t) => t.status !== 'live');
          return (
            <section key={group}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2">{TOOL_GROUP_LABELS[group]}</div>
              <div className="space-y-3">
                {liveTools.map((t) => {
                  const Icon = getIcon(t.icon);
                  const list = grants[t.key] || [];
                  const enabledIds = new Set(list.map((g) => g.tenantId));
                  const count = grants[t.key] ? list.length : t.tenantCount;
                  return (
                    <div key={t.key} className="bg-white rounded-xl border-2 overflow-hidden" style={{ borderColor: 'rgba(220,20,60,0.4)' }}>
                      <div className="p-5 flex flex-wrap items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
                          <Icon size={22} />
                        </div>
                        <div className="flex-1 min-w-[240px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="font-semibold text-stone-900">{t.name}</h2>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[t.status].cls}`}>{STATUS_PILL[t.status].label}</span>
                            {t.isNew && <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#DC143C] rounded-full px-2 py-0.5">New</span>}
                          </div>
                          <p className="text-sm text-stone-500 mt-1">{t.description}</p>
                          <ContractRow tool={t} />
                          <div className="text-xs text-stone-400 mt-1">
                            Tool key <code>{t.key}</code>{t.merchantRoute ? ' · appears in the merchant sidebar under Tools' : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPickerFor(pickerFor === t.key ? null : t.key)}
                          className="h-11 px-4 rounded-lg text-white text-sm font-medium flex items-center gap-2"
                          style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
                        >
                          <Plus size={16} />Enable for tenant
                        </button>
                      </div>

                      <div className="border-t border-stone-100">
                        <div className="px-5 py-2 text-xs text-stone-400 bg-stone-50">
                          Enabled for {count} tenant{count === 1 ? '' : 's'}
                        </div>
                        {loading && !grants[t.key] ? (
                          <RubyLoader variant="section" label="Loading enabled tenants" />
                        ) : list.length === 0 ? (
                          <div className="px-5 py-4 text-sm text-stone-400">No tenants yet.</div>
                        ) : (
                          <div className="divide-y divide-stone-100">
                            {list.map((g) => {
                              const name = g.tenantName || g.tenantId;
                              const by = g.enabledByEmail || g.enabledBy;
                              return (
                                <div key={g.tenantId} className="px-5 py-2 flex items-center gap-4 text-sm min-h-[56px]">
                                  <div className="w-8 h-8 rounded-full bg-stone-900 text-white grid place-items-center text-xs font-semibold shrink-0">{initials(name)}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-stone-900 truncate">{name}</div>
                                    <div className="text-xs text-stone-400 truncate">
                                      {g.enabledAt ? `Enabled ${fmtDate(g.enabledAt)}` : 'Enabled'}{by ? ` by ${by}` : ''}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/tenant/${encodeURIComponent(g.tenantId)}`)}
                                    className="h-11 px-3 rounded-lg border border-stone-300 text-xs text-stone-700 hover:bg-stone-50 hidden sm:flex items-center gap-1.5"
                                  >
                                    <ExternalLink size={14} />Open tenant
                                  </button>
                                  <Switch
                                    on
                                    busy={disabling && confirm?.grant.tenantId === g.tenantId && confirm?.toolKey === t.key}
                                    label={`Disable ${t.name} for ${name}`}
                                    onClick={() => setConfirm({ toolKey: t.key, toolName: t.name, grant: g })}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {pickerFor === t.key && (
                        <TenantPicker
                          enabledIds={enabledIds}
                          onPick={(hit) => enable(t.key, hit)}
                          onClose={() => setPickerFor(null)}
                        />
                      )}
                    </div>
                  );
                })}

                {otherTools.length > 0 && (
                  <div className="grid md:grid-cols-2 gap-3">
                    {otherTools.map((t) => {
                      const Icon = getIcon(t.icon);
                      return (
                        <div key={t.key} className="bg-white rounded-xl border border-stone-200 p-4 flex gap-3 opacity-75">
                          <div className="w-11 h-11 rounded-xl bg-stone-100 text-stone-500 grid place-items-center shrink-0">
                            <Icon size={18} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-stone-900">{t.name}</span>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[t.status].cls}`}>{STATUS_PILL[t.status].label}</span>
                            </div>
                            <div className="text-xs text-stone-500 mt-0.5">{t.description}</div>
                            <div className="text-xs text-stone-400 mt-1">{t.tenantCount || 0} tenants</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {confirm && (
        <ConfirmModal
          title={`Disable ${confirm.toolName}?`}
          body={
            <>
              <b className="text-stone-700">{confirm.grant.tenantName || confirm.grant.tenantId}</b> will lose access to this tool
              straight away. You can enable it again at any time.
            </>
          }
          confirmLabel="Disable"
          busy={disabling}
          onConfirm={disable}
          onCancel={() => { if (!disabling) setConfirm(null); }}
        />
      )}
    </InventoryPage>
  );
};

export default ToolLibraryPage;
